<script setup lang="ts">
import { onMounted, ref, watch, computed } from "vue";
import { bemBuilder, debounce } from "@chatfood/core-utils";
import {
  AtomCard,
  AtomText,
  SatInputSearch,
  SatElasticPanel,
  AtomButton,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import {
  BASE_TELEPORT_SELECTOR,
  useBugReporter,
  useBusinessStore,
  useDirectBackend,
} from "#imports";
import { useLocale } from "~/composables/use-locale";
import type { IDomainPaginatedList } from "~/domain/paginated-list";
import type { IDomainOutlet } from "~/domain/outlet";
import OutletDetails from "~/components/outlet-details/OutletDetails.vue";

const css = bemBuilder("page-locations");
const { businessRepo } = useDirectBackend();
const business = useBusinessStore();
const { t } = useLocale();
const { report } = useBugReporter();

const search = ref("");
const isLoading = ref(false);
const locations = ref<IDomainPaginatedList<IDomainOutlet>>();
const chosenLocation = ref<IDomainOutlet>();
const currentPage = ref(1);
const hasMore = ref(true);
const loadMoreClicked = ref(false);
const showOutletDetails = ref(false);

const showLoadMoreButton = computed(
  () => hasMore.value && locations.value?.data?.length,
);
const showNoMoreMessage = computed(
  () =>
    loadMoreClicked.value &&
    locations.value &&
    locations.value.data &&
    locations.value.data.length > 0 &&
    !hasMore.value,
);
const showLoadMoreArea = computed(
  () => showLoadMoreButton.value || showNoMoreMessage.value,
);

// Helper to reset locations and page
const resetLocations = () => {
  locations.value = undefined;
  currentPage.value = 1;
  hasMore.value = true;
};

const fetchLocations = async (opts?: {
  append?: boolean;
  searchValue?: string;
  fromLoadMore?: boolean;
}) => {
  if (isLoading.value || !hasMore.value) return;
  if (opts?.fromLoadMore) loadMoreClicked.value = true;
  isLoading.value = true;
  try {
    const res = await businessRepo.getOutlets({
      businessId: business.id,
      salesChannel: business.salesChannel,
      search:
        opts?.searchValue ??
        (search.value.trim() === "" ? undefined : search.value),
      perPage: 10,
      page: currentPage.value,
    });

    if (opts?.append && locations.value) {
      locations.value = {
        ...res,
        data: [...locations.value.data, ...res.data],
      };
    } else {
      locations.value = { ...res, data: [...res.data] };
    }

    // If less than perPage, no more data
    hasMore.value = res.data.length === 10;
    if (hasMore.value) {
      currentPage.value += 1;
    }
  } catch (e) {
    report(e);
  } finally {
    isLoading.value = false;
  }
};

// Debounced search handler
const debouncedSearch = debounce(() => {
  resetLocations();
  loadMoreClicked.value = false;
  fetchLocations({
    append: false,
    searchValue: search.value.trim() === "" ? undefined : search.value,
  });
}, 500);

watch(
  () => search.value,
  () => {
    debouncedSearch();
  },
);

const onShowLocationDetails = (location: IDomainOutlet) => {
  if (!location) return;
  showOutletDetails.value = true;
  chosenLocation.value = location;
};

onMounted(() => {
  resetLocations();
  loadMoreClicked.value = false;
  fetchLocations();
});
</script>

<template>
  <div :class="css()">
    <AtomCard padding="large" :class="css('card')">
      <div :class="css('wrapper')">
        <AtomText type="subtitle" color="color-text-secondary"
          >{{ t("pages.locations.title") }}
        </AtomText>
        <SatInputSearch
          v-model="search"
          :placeholder="t('pages.locations.search_placeholder')"
        />
        <div :class="css('location-wrapper')">
          <AtomLoadingSpinner
            v-if="isLoading && !locations?.data?.length"
            :class="css('loading')"
          />
          <template v-else-if="locations?.data?.length">
            <a
              v-for="location in locations.data"
              :key="location.id"
              :class="css('location')"
              href="#"
              @click.prevent="onShowLocationDetails(location)"
            >
              <AtomText type="body-bold">
                {{ location.name }}
              </AtomText>
              <AtomText type="body" color="color-text-secondary">
                {{ location.address.location }}
              </AtomText>
            </a>
          </template>
          <template
            v-else-if="
              !isLoading &&
              locations &&
              (!locations.data || locations.data.length === 0)
            "
          >
            <AtomText color="color-text-disabled" :class="css('loading')">
              {{ t("pages.locations.no_records_found") }}
            </AtomText>
          </template>
        </div>
      </div>
      <div v-if="showLoadMoreArea" :class="css('load-more-area')">
        <AtomButton
          v-if="showLoadMoreButton"
          :is-loading="isLoading"
          theme="ghost"
          :disabled="isLoading"
          @click="fetchLocations({ append: true, fromLoadMore: true })"
        >
          {{ t("pages.locations.load_more_cta") }}
        </AtomButton>
        <AtomText v-else-if="showNoMoreMessage" color="color-text-disabled">
          {{ t("pages.locations.no_more_records_message") }}
        </AtomText>
      </div>
      <Teleport :to="BASE_TELEPORT_SELECTOR">
        <SatElasticPanel
          v-model="showOutletDetails"
          show-close
          width-fit-content
        >
          <OutletDetails v-if="chosenLocation" :outlet-id="chosenLocation.id" />
        </SatElasticPanel>
      </Teleport>
    </AtomCard>
  </div>
</template>

<style scoped lang="scss">
.page-locations {
  @media (width >= $breakpoint-sm) {
    padding: var(--spacing-2xl);
  }

  &__card {
    max-width: 640px;
    margin: 0 auto;
    gap: var(--spacing-lg);
    display: flex;
    flex-direction: column;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__location-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__loading {
    display: flex;
    margin: var(--spacing-lg) auto;
  }

  &__location {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;
    gap: var(--spacing-xs);
    border: solid 1px var(--color-base-80);
    padding: var(--spacing-md);
    border-radius: var(--radii-card-sm);
  }

  &__load-more-area {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
